/**
 * SEO metadata optimized for medical-condition search intent.
 * Targets queries like: "cura para X", "tratamiento natural X", "cómo curar X",
 * "remedio natural X", "X cómo se cura", "qué hacer si tengo X".
 *
 * Each entry includes:
 *  - title: <title> override (~55-65 chars for SERPs)
 *  - description: meta description (~140-160 chars)
 *  - keywords: comma-list (legacy + signal)
 *  - condition: schema.org MedicalCondition (for medical knowledge graph)
 *  - lsi: LSI/semantic keywords used in alt text and aria
 */

export const SEO_METADATA = {
  /* --- Padecimientos --- */
  "diabetes-tipo-2-y-glutation": {
    title: "Diabetes tipo 2: cómo proteger tus tejidos del daño silencioso",
    description: "¿Buscas cómo revertir o controlar la diabetes tipo 2 de forma natural? Descubre el rol del glutatión, suplementos respaldados por ciencia y protocolo integral.",
    keywords: "diabetes tipo 2 tratamiento natural, cómo controlar la diabetes, revertir diabetes, glutatión diabetes, Immunocal diabetes, neuropatía diabética natural, sensibilidad a la insulina, prediabetes",
    condition: { name: "Diabetes mellitus tipo 2", code: "E11" },
    lsi: ["resistencia a la insulina", "complicaciones diabéticas", "glicemia alta", "hemoglobina glicosilada"],
  },
  "hipertension-y-antioxidantes": {
    title: "Hipertensión: tratamiento natural y el papel de los antioxidantes",
    description: "¿Buscas cómo bajar la presión alta naturalmente? Conoce el rol del glutatión y óxido nítrico en tus arterias, alimentos y suplementos con evidencia.",
    keywords: "hipertensión tratamiento natural, cómo bajar la presión alta, remedios naturales presión, presión arterial alta, antioxidantes hipertensión, óxido nítrico, omega 3 presión",
    condition: { name: "Hipertensión arterial esencial", code: "I10" },
    lsi: ["presión sistólica", "endotelio", "vasodilatación", "remodelado vascular"],
  },
  "tiroides-hipotiroidismo-hashimoto": {
    title: "Hashimoto e hipotiroidismo: cómo apoyar tu tiroides naturalmente",
    description: "¿Buscas tratamiento natural para Hashimoto o hipotiroidismo? Glutatión, selenio y la conexión autoinmune. Protocolo de soporte sin suspender medicación.",
    keywords: "hashimoto tratamiento natural, hipotiroidismo cura natural, tiroides autoinmune, selenio tiroides, glutatión hashimoto, anticuerpos anti-TPO, levotiroxina natural",
    condition: { name: "Tiroiditis de Hashimoto", code: "E06.3" },
    lsi: ["TSH alta", "T4 libre", "T3", "tiroides lenta"],
  },
  "gastritis-cronica-y-glutation": {
    title: "Gastritis crónica: cómo sanar la mucosa gástrica de raíz",
    description: "¿Buscas cómo curar la gastritis crónica? Más allá del omeprazol: el rol del glutatión, H. pylori, alimentos sanadores y protocolo de 90 días.",
    keywords: "gastritis crónica tratamiento natural, cómo curar gastritis, gastritis sin omeprazol, H pylori natural, mucosa gástrica, glutatión gastritis, DGL regaliz",
    condition: { name: "Gastritis crónica", code: "K29" },
    lsi: ["reflujo", "acidez estomacal", "úlcera gástrica", "helicobacter pylori"],
  },
  "colitis-disbiosis-intestinal": {
    title: "Colitis: tratamiento natural y protocolo 5R de medicina funcional",
    description: "¿Buscas cómo curar la colitis o el SII? Protocolo 5R, microbiota, disbiosis intestinal y suplementos con evidencia para reparar tu intestino.",
    keywords: "colitis tratamiento natural, cómo curar colitis, SII tratamiento, disbiosis intestinal, microbiota colitis, intestino permeable, probióticos colitis, glutatión intestinal",
    condition: { name: "Síndrome del intestino irritable", code: "K58" },
    lsi: ["colon irritable", "inflamación intestinal", "permeabilidad intestinal", "butirato"],
  },
  "higado-graso-no-alcoholico-nafld": {
    title: "Hígado graso: cómo revertir NAFLD de forma natural (paso a paso)",
    description: "¿Buscas cómo curar el hígado graso? El NAFLD es reversible. Protocolo de detox real, dieta, glutatión y suplementos respaldados por ciencia.",
    keywords: "hígado graso tratamiento, cómo curar hígado graso, NAFLD natural, revertir hígado graso, esteatosis hepática, glutatión hígado, dieta hígado graso",
    condition: { name: "Enfermedad del hígado graso no alcohólico (NAFLD)", code: "K76.0" },
    lsi: ["esteatosis hepática", "transaminasas altas", "ALT AST", "cirrosis prevención"],
  },
  "sindrome-metabolico-y-obesidad": {
    title: "Síndrome metabólico: cómo revertirlo en 90 días (protocolo real)",
    description: "¿Buscas cómo bajar grasa visceral, glucosa y triglicéridos? Estrategia integral 90 días para revertir el síndrome metabólico antes de la diabetes.",
    keywords: "síndrome metabólico tratamiento, revertir prediabetes, bajar grasa visceral, resistencia a la insulina natural, triglicéridos altos, perder peso saludable, ayuno intermitente",
    condition: { name: "Síndrome metabólico", code: "E88.81" },
    lsi: ["prediabetes", "obesidad central", "HOMA-IR", "circunferencia abdominal"],
  },
  "artritis-dolor-articular-cronico": {
    title: "Artritis: protocolo antiinflamatorio natural para el dolor articular",
    description: "¿Buscas cómo aliviar la artritis sin tanto medicamento? Protocolo antiinflamatorio integral: glutatión, omega-3, cúrcuma y hábitos respaldados por evidencia.",
    keywords: "artritis tratamiento natural, dolor articular crónico, cómo aliviar la artritis, omega 3 articulaciones, cúrcuma artritis, artritis reumatoide natural, osteoartritis",
    condition: { name: "Artritis", code: "M19" },
    lsi: ["inflamación articular", "rigidez matutina", "cartílago", "AINEs alternativa"],
  },
  "fibromialgia-fatiga-cronica": {
    title: "Fibromialgia y fatiga crónica: el problema mitocondrial detrás",
    description: "¿Buscas tratamiento natural para fibromialgia o síndrome de fatiga crónica? Disfunción mitocondrial, CoQ10, glutatión y protocolo de recuperación gradual.",
    keywords: "fibromialgia tratamiento natural, fatiga crónica cura, síndrome fatiga crónica, CoQ10 fibromialgia, glutatión fatiga, mitocondrias, dolor difuso crónico",
    condition: { name: "Fibromialgia", code: "M79.7" },
    lsi: ["dolor crónico generalizado", "agotamiento crónico", "encefalomielitis miálgica", "puntos sensibles"],
  },
  "ansiedad-depresion-eje-intestino-cerebro": {
    title: "Ansiedad y depresión: el eje intestino-cerebro que cambia todo",
    description: "¿Buscas tratamiento natural para ansiedad o depresión? El 90% de la serotonina vive en tu intestino. Protocolo integral con base científica.",
    keywords: "ansiedad tratamiento natural, depresión cura natural, eje intestino cerebro, microbiota ansiedad, serotonina intestino, omega 3 depresión, vitamina D depresión",
    condition: { name: "Trastornos del estado de ánimo", code: "F30-F39" },
    lsi: ["serotonina baja", "neuroinflamación", "psicobióticos", "estado de ánimo bajo"],
  },

  /* --- Salud / temas educativos --- */
  "menopausia-y-glutation": {
    title: "Menopausia: cómo aliviar síntomas con glutatión y antioxidantes",
    description: "¿Buscas cómo aliviar los síntomas de la menopausia naturalmente? La conexión estrógeno-glutatión, calores, fatiga y protocolo sin hormonas.",
    keywords: "menopausia tratamiento natural, síntomas menopausia, calores menopausia natural, perimenopausia, glutatión menopausia, mujer 40 años, hormonas naturales",
    condition: { name: "Menopausia y perimenopausia", code: "N95" },
    lsi: ["sofocos", "estrógeno bajo", "perimenopausia", "salud ósea menopausia"],
  },
  "como-tomar-immunocal-correctamente": {
    title: "Cómo tomar Immunocal correctamente: 7 errores y guía completa",
    description: "Guía paso a paso para tomar Immunocal correctamente y maximizar el glutatión intracelular. Los 7 errores más comunes y el protocolo ideal.",
    keywords: "cómo tomar immunocal, immunocal dosis, immunocal en ayunas, immunocal con agua, errores immunocal, immunocal mañana, protocolo immunocal",
    condition: null,
    lsi: ["proteína no desnaturalizada", "cisteína bioactiva", "Bounous", "patente médica"],
  },
  "niebla-mental-causas-y-solucion": {
    title: "Niebla mental: 6 causas reales y cómo recuperar la claridad",
    description: "¿Buscas cómo quitar la niebla mental? Conoce las 6 causas reales (inflamación, glucosa, sueño, déficits, estrés, microbiota) y un protocolo de 21 días.",
    keywords: "niebla mental tratamiento, cómo quitar la niebla mental, brain fog cura, falta de concentración, memoria corto plazo, neuroinflamación, B12 cerebro",
    condition: { name: "Deterioro cognitivo leve", code: "G31.84" },
    lsi: ["confusión mental", "lentitud cognitiva", "memoria de trabajo", "claridad mental"],
  },
  "inflamacion-silenciosa-fatiga-peso-dolor": {
    title: "Inflamación silenciosa: causa oculta de fatiga, peso y dolor",
    description: "¿Buscas la causa raíz de tu cansancio, peso que no baja y dolor inexplicado? La inflamación crónica de bajo grado y cómo apagarla en 8 frentes.",
    keywords: "inflamación crónica tratamiento, cómo bajar la inflamación, PCR alta natural, inflamación silenciosa, omega 3 inflamación, dieta antiinflamatoria",
    condition: { name: "Inflamación crónica de bajo grado", code: "R65.10" },
    lsi: ["citoquinas inflamatorias", "PCR ultrasensible", "TNF-alfa", "IL-6"],
  },
  "estres-oxidativo-explicado": {
    title: "Estrés oxidativo: qué es, síntomas y cómo combatirlo (guía)",
    description: "Guía completa sobre estrés oxidativo: qué es, cómo daña tu cuerpo, señales tempranas y los suplementos con evidencia para combatirlo.",
    keywords: "estrés oxidativo qué es, radicales libres, antioxidantes guía, daño celular, envejecimiento celular, estrés oxidativo síntomas",
    condition: null,
    lsi: ["ROS especies reactivas", "antioxidantes endógenos", "glutatión peroxidasa", "envejecimiento"],
  },
  "que-es-el-glutation-antioxidante-maestro": {
    title: "Qué es el glutatión: el antioxidante maestro (guía completa)",
    description: "Guía completa del glutatión, el antioxidante maestro de cada célula. Funciones, deficiencia, cómo elevarlo y por qué importa para tu salud.",
    keywords: "qué es el glutatión, glutatión beneficios, antioxidante maestro, cómo aumentar glutatión, glutatión bajo síntomas, GSH",
    condition: null,
    lsi: ["tripéptido", "cisteína glicina glutamato", "redox celular", "Fase II hepática"],
  },

  /* --- Nuevos jun-2026 --- */
  "frutos-rojos-salud-de-la-mujer": {
    title: "Frutos rojos y salud de la mujer: vitalidad femenina con antocianinas",
    description: "¿Por qué los frutos rojos son el alimento más estudiado para mujeres +35? Antocianinas, fitoestrógenos suaves, corazón, piel y cerebro femenino.",
    keywords: "frutos rojos beneficios mujer, antocianinas, frutos rojos menopausia, polifenoles salud femenina, arándanos mujer, granada mujer, alimentos antiedad",
    condition: null,
    lsi: ["antocianinas", "polifenoles femeninos", "fitoestrógenos", "salud cardiovascular mujer"],
  },
  "optimizer-performance-3-cafeinas-deportistas": {
    title: "Optimizer Performance: las 3 cafeínas para rendimiento deportivo",
    description: "Conoce las 3 fuentes de cafeína de Optimizer Performance (guaraná, anhidra, té verde con L-Teanina) y cómo dan energía sostenida sin crash.",
    keywords: "optimizer performance immunotec, cafeína deportistas, pre-entreno natural, guaraná cafeína, L-teanina, rendimiento deportivo natural, energía sin crash",
    condition: null,
    lsi: ["pre-entreno", "ergogénico", "rendimiento físico", "energía sostenida"],
  },
  "knutric-diabetes-control-glucemico": {
    title: "Knutric y diabetes: control glucémico natural (fibra + nopal + cromo)",
    description: "Cómo Knutric de Immunotec apoya a personas con diabetes tipo 2: reduce picos posprandiales, mejora sensibilidad a la insulina y baja HbA1c.",
    keywords: "knutric diabetes, knutric immunotec, control glucémico natural, fibra diabetes, nopal diabetes, cromo insulina, picos posprandiales, HbA1c natural",
    condition: { name: "Diabetes mellitus tipo 2", code: "E11" },
    lsi: ["hemoglobina glicosilada", "glucosa posprandial", "sensibilidad a la insulina", "fibra soluble"],
  },
};

export function getSEO(slug) {
  return SEO_METADATA[slug] || null;
}
